import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import type { Tables } from "@/integrations/supabase/types";

type TrainingGroup = Tables<"training_groups">;

interface MemberRow {
  id: string;
  player_id: string;
  status: string;
  parent_id: string | null;
  profiles: { full_name: string | null } | null;
}

const STATUS_LABEL_KEYS: Record<string, string> = {
  active: "seasons.groups.statusActive",
  waitlist: "seasons.groups.statusWaitlist",
  removed: "seasons.groups.statusRemoved",
};

export default function GroupMembers({
  group,
  onBack,
}: {
  group: TrainingGroup;
  onBack: () => void;
}) {
  const { t } = useTranslation("admin");
  const queryClient = useQueryClient();
  const { organization } = useOrganization();
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["group-members", group.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("group_members")
        .select("id, player_id, status, parent_id, profiles!group_members_player_id_fkey(full_name)")
        .eq("group_id", group.id)
        .neq("status", "removed")
        .order("joined_at");
      if (error) throw error;
      return data as unknown as MemberRow[];
    },
  });

  // Search profiles to add
  const { data: searchResults = [] } = useQuery({
    queryKey: ["profile-search", search],
    queryFn: async () => {
      if (search.length < 2) return [];
      const { data } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name")
        .ilike("full_name", `%${search}%`)
        .limit(10);
      const memberIds = new Set(members.map((m) => m.player_id));
      return (data ?? []).filter((p) => !memberIds.has(p.id));
    },
    enabled: showAdd && search.length >= 2,
  });

  const addMutation = useMutation({
    mutationFn: async (playerId: string) => {
      const { error } = await supabase.from("group_members").insert([{
        group_id: group.id,
        player_id: playerId,
        status: "active",
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-members", group.id] });
      toast.success(t("admin:toast.playerAdded"));
      setSearch("");
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotAddPlayer")); },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("group_members")
        .update({ status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-members", group.id] });
      toast.success(t("admin:toast.statusUpdated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotUpdatePlayer")); },
  });

  const removeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("group_members")
        .update({ status: "removed" })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["group-members", group.id] });
      queryClient.invalidateQueries({ queryKey: ["admin-group-member-counts"] });
      toast.success(t("admin:toast.playerRemoved"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("toast.couldNotRemovePlayer")); },
  });

  return (
    <div className="space-y-4">
      <Button variant="ghost" size="sm" onClick={onBack} className="gap-1">
        <ArrowLeft className="h-4 w-4" /> {t("seasons.groups.backToGroups")}
      </Button>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">{t("seasons.groups.players")} — {group.name}</h2>
        <Button size="sm" onClick={() => setShowAdd(!showAdd)} className="gap-1">
          <Plus className="h-4 w-4" /> {t("seasons.groups.addPlayer")}
        </Button>
      </div>

      {showAdd && (
        <div className="border rounded-md p-3 space-y-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("seasons.groups.searchPlayer")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="space-y-1">
              {searchResults.map((p) => (
                <div key={p.id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted">
                  <span className="text-sm">{p.full_name || p.id.slice(0, 8)}</span>
                  <Button size="sm" variant="outline" onClick={() => addMutation.mutate(p.id)}>
                    {t("seasons.groups.add")}
                  </Button>
                </div>
              ))}
            </div>
          )}
          {search.length >= 2 && searchResults.length === 0 && (
            <p className="text-sm text-muted-foreground">{t("seasons.groups.noResults")}</p>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("seasons.loading")}</p>
      ) : members.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("seasons.groups.noPlayersInGroup")}</p>
      ) : (
        <div className="space-y-1">
          {members.map((m) => (
            <div key={m.id} className="flex items-center justify-between py-2 px-3 border rounded-md">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">
                  {m.profiles?.full_name || m.player_id.slice(0, 8)}
                </span>
                <Badge variant={m.status === "active" ? "default" : "secondary"}>
                  {t(STATUS_LABEL_KEYS[m.status]) ?? m.status}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <Select
                  value={m.status}
                  onValueChange={(v) => updateStatusMutation.mutate({ id: m.id, status: v })}
                >
                  <SelectTrigger className="w-28 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">{t("seasons.groups.statusActive")}</SelectItem>
                    <SelectItem value="waitlist">{t("seasons.groups.statusWaitlist")}</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => removeMutation.mutate(m.id)}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
